import * as React from "react";
import { observer, inject } from "mobx-react";
import { toJS } from "mobx";

import { InvoiceLineItem, EntryType } from "src/model/Financial";
import { TextareaGroup, SelectGroup, InputGroup } from "../common/SimpleFormComponents";
import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { EditableInvoiceLineItem } from "./InvoiceDialog";

export interface PageState {
    wasValidated: boolean;
}
export interface Props {
    configurationStore?: ConfigurationStore;
    invoiceLineItem: EditableInvoiceLineItem.Fields | null;
    closeInlineItemForm: () => void;
    addOrEditInvoiceLineItems: (lineItem: EditableInvoiceLineItem.Fields) => void;
    artificialLineItemId: number;
}

type State = PageState & EditableInvoiceLineItem.Fields;

@inject("configurationStore")
@observer
export default class InvoiceLineItemForm extends React.Component<Props, State> {
    private formLineItem: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            wasValidated: false,
            ...(props.invoiceLineItem
                ? JSON.parse(JSON.stringify(props.invoiceLineItem))
                : EditableInvoiceLineItem.Empty(this.props.artificialLineItemId))
        };
    }

    submit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.formLineItem && this.formLineItem.checkValidity() && this.state.entryTypeId !== null) {
            this.setState({ wasValidated: false });
            this.props.addOrEditInvoiceLineItems(this.state);
            this.closeInlineItemForm();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    componentDidUpdate(prevProps: Props) {
        if (this.props.invoiceLineItem !== null && prevProps.invoiceLineItem !== this.props.invoiceLineItem) {
            // Make a copy of the object so that changes don't propagate back to the InvoiceDialog
            this.setState({ ...JSON.parse(JSON.stringify(this.props.invoiceLineItem)) });
        }
    }

    closeInlineItemForm = () => {
        this.props.closeInlineItemForm();
    };

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            throw new Error(" ** No Configuration Store **");
        }

        const saleTaxes = configurationStore.salesTaxes;

        return (
            <div className="form-row">
                <div className="col-12 px-3">
                    <div className="card mx-0 px-0 mb-4" style={{ backgroundColor: "#f7faff" }}>
                        <div className="card-header py-1"> {this.props.invoiceLineItem ? "Edit line item" : "Create line item"} </div>
                        <div className="card-body px-3 py-3">
                            <form
                                className={"px-2 needs-validation " + (this.state.wasValidated === true ? "was-validated" : "")}
                                noValidate={true}
                                onSubmit={this.submit}
                                ref={ref => (this.formLineItem = ref)}
                                id="lineItemForm"
                            >
                                <div className="form-row mb-3">
                                    <SelectGroup
                                        label="Type:"
                                        name="Type"
                                        isRequired={true}
                                        isInvalid={this.state.wasValidated && this.state.entryTypeId === null}
                                        className="col-md-12 col-lg-6"
                                        disabled={false}
                                        value={this.state.entryTypeId || ""}
                                        onChange={e => {
                                            const entryTypeId = parseInt(e.target.value, 10);
                                            this.setState({ entryTypeId });
                                        }}
                                    >
                                        {!this.state.entryTypeId && <option value="">Select type...</option>}
                                        {configurationStore!.entryTypes
                                            .slice()
                                            .sort((a: EntryType, b: EntryType) =>
                                                a.debitCredit === b.debitCredit
                                                    ? a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                                                    : a.debitCredit.localeCompare(b.debitCredit)
                                            )
                                            .map((c: EntryType, i: number) => (
                                                <option key={i} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                    </SelectGroup>
                                    <InputGroup
                                        label="Amount:"
                                        className="col-md-12 col-lg-6"
                                        type="text"
                                        name="amount"
                                        isRequired={true}
                                        disabled={this.state.entryTypeId === null}
                                        value={this.state.amount || ""}
                                        onChange={e => this.setState({ amount: e.target.value })}
                                    />
                                </div>
                                <div className="form-row">
                                    <TextareaGroup
                                        label="Details"
                                        rows={2}
                                        isRequired={true}
                                        name="notes"
                                        className="col-md-12 col-lg-6"
                                        value={this.state.description || ""}
                                        onChange={e => this.setState({ description: e.target.value })}
                                    />
                                    <div className="col-md-12 col-lg-6">
                                        <label className="form-check-label ml-2" htmlFor="applicableTaxesHST">
                                            Taxes: <span className="text-danger">*</span>
                                        </label>
                                        <div className="form-group form-check ml-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={
                                                    this.state.applicableTaxes ? this.state.applicableTaxes.some(tax => tax.name === "HST") : false
                                                }
                                                required={false}
                                                onChange={() => {
                                                    const taxesIds = this.state.applicableTaxes;
                                                    if (taxesIds && taxesIds.some(tax => tax.name === "HST")) {
                                                        //Remove HST
                                                        this.setState({ applicableTaxes: taxesIds.filter(tax => tax.name !== "HST") });
                                                    } else {
                                                        //Add HST
                                                        const hstSaleTaxes = configurationStore.salesTaxes.find(taxes => taxes.name === "HST");
                                                        if (hstSaleTaxes) {
                                                            this.setState({ applicableTaxes: [...taxesIds, hstSaleTaxes] });
                                                        }
                                                    }
                                                }}
                                            />
                                            <label className="form-check-label ml-2" htmlFor="applicableTaxesHST">
                                                HST
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-row text-right">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm ml-auto mr-3"
                                        onClick={() => this.closeInlineItemForm()}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary btn-sm" form="lineItemForm">
                                        {this.props.invoiceLineItem ? "Edit line item" : "Add line item"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/InvoiceLineItemForm.tsx